<template>
  <div class="pin-pad">
    <div class="pin-pad__dots">
      <span
        v-for="i in PIN_LENGTH"
        :key="i"
        class="pin-pad__dot"
        :class="{ 'pin-pad__dot--filled': i <= pin.length }"
      />
    </div>

    <p v-if="error" class="pin-pad__error text-body-small error--text text-center mb-2">
      {{ error }}
    </p>

    <div class="pin-pad__grid">
      <v-btn
        v-for="digit in DIGIT_ROWS"
        :key="digit"
        text
        class="pin-pad__key text-h2"
        :disabled="disabled"
        @click="pressDigit(digit)"
      >
        {{ digit }}
      </v-btn>
      <div class="pin-pad__key--spacer" />
      <v-btn text class="pin-pad__key text-h2" :disabled="disabled" @click="pressDigit('0')">
        0
      </v-btn>
      <v-btn text class="pin-pad__key" :disabled="disabled || pin.length === 0" @click="backspace">
        <v-icon>mdi-backspace-outline</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';

const PIN_LENGTH = 6;
const DIGIT_ROWS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

defineProps<{
  error?: string | null;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: 'complete', pin: string): void;
}>();

const pin = ref('');

function pressDigit(digit: string) {
  if (pin.value.length >= PIN_LENGTH) {
    return;
  }
  pin.value += digit;
  if (pin.value.length === PIN_LENGTH) {
    emit('complete', pin.value);
  }
}

function backspace() {
  pin.value = pin.value.slice(0, -1);
}

function reset() {
  pin.value = '';
}

defineExpose({ reset });
</script>

<style scoped lang="scss">
$dot-size: 14px;
$key-size: 72px;

.pin-pad {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;

  &__dots {
    display: flex;
    gap: 16px;
  }

  &__dot {
    width: $dot-size;
    height: $dot-size;
    border-radius: 50%;
    border: 2px solid currentColor;
    transition: background-color 0.1s;

    &--filled {
      background-color: currentColor;
    }
  }

  &__error {
    min-height: 20px;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(3, $key-size);
    gap: 8px;
    justify-content: center;

    &::v-deep {
      .v-btn {
        &:before {
          background-color: transparent;
        }

        &:hover,
        &:focus {
          &:before {
            background-color: rgba($primary, 0.25);
          }
        }
      }
    }
  }

  &__key {
    min-width: 0 !important;
    width: $key-size !important;
    height: $key-size !important;
    border-radius: 50% !important;
  }

  &__key--spacer {
    width: $key-size;
    height: $key-size;
  }
}
</style>
